import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold, Italic, Underline as UnderlineIcon, Highlighter,
  Heading1, Heading2, Heading3,
  List, ListOrdered, Quote, Undo2, Redo2, Link2, Link2Off, X,
} from 'lucide-react';
import { api } from '@/lib/api';

/**
 * Shared TipTap-powered rich text editor used across the CMS.
 *
 * - Emits HTML via onChange (string). Empty content = ''.
 * - Public renderers should always pass HTML through sanitizeHtml() from
 *   '@/lib/sanitize' before rendering with dangerouslySetInnerHTML.
 * - Includes an Internal Link picker that lets an admin search across
 *   channels / articles / products / mobile reads and insert a
 *   permalink as a normal <a href> (target=_self, rel=internal).
 */
export default function RichTextEditor({
  value,
  onChange,
  placeholder = 'Start writing…',
  minHeight = 260,
  testId,
  disabled = false,
}) {
  const editor = useEditor({
    editable: !disabled,
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        codeBlock: { HTMLAttributes: { class: 'pg-code-block' } },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: 'noopener noreferrer' },
        // Only allow http(s)/mailto/tel/anchor links
        validate: (href) => /^(?:https?|mailto|tel|\/|#)/i.test(href),
      }),
      Highlight.configure({ multicolor: false }),
      Placeholder.configure({ placeholder, showOnlyWhenEditable: true }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // TipTap emits <p></p> for empty content — normalize to ''.
      onChange?.(html === '<p></p>' ? '' : html);
    },
  });

  // Sync external value changes (e.g. when switching between items in a drawer).
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || '';
    // Guard against overwriting mid-typing when identical
    if (current !== incoming && !(current === '<p></p>' && incoming === '')) {
      editor.commands.setContent(incoming || '', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const [linkModal, setLinkModal] = useState(null); // { url, type: 'external'|'internal' }

  const openExternalLink = () => {
    const prev = editor?.getAttributes('link').href || '';
    setLinkModal({ mode: 'external', url: prev });
  };

  const openInternalLink = () => setLinkModal({ mode: 'internal', url: '' });

  const applyLink = useCallback((href, opts = {}) => {
    if (!editor) return;
    if (!href) {
      editor.chain().focus().unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({
        href,
        target: opts.internal ? '_self' : '_blank',
        rel: opts.internal ? 'internal' : 'noopener noreferrer',
      }).run();
    }
    setLinkModal(null);
  }, [editor]);

  const btn = (active, disabled, onClick, label, Icon, testid) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      aria-label={label}
      title={label}
      className={`px-2 py-1.5 rounded-[var(--pg-radius-sm)] text-xs border transition-colors inline-flex items-center gap-1 ${active
        ? 'bg-[rgba(255,62,165,0.14)] border-[var(--pg-neon-pink)] text-[var(--pg-neon-pink)]'
        : 'bg-transparent border-[var(--pg-border-2)] text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)]'}`}
    >
      {Icon && <Icon size={14} />}
    </button>
  );

  if (!editor) return null;

  return (
    <div className="pg-rte" data-testid={testId || 'rich-text-editor'}>
      <div className="flex flex-wrap items-center gap-1 pg-card !p-2 mb-2" role="toolbar" aria-label="Text formatting">
        {btn(editor.isActive('heading', { level: 1 }), false, () => editor.chain().focus().toggleHeading({ level: 1 }).run(), 'Heading 1', Heading1, 'rte-h1')}
        {btn(editor.isActive('heading', { level: 2 }), false, () => editor.chain().focus().toggleHeading({ level: 2 }).run(), 'Heading 2', Heading2, 'rte-h2')}
        {btn(editor.isActive('heading', { level: 3 }), false, () => editor.chain().focus().toggleHeading({ level: 3 }).run(), 'Heading 3', Heading3, 'rte-h3')}
        <span className="w-px h-5 mx-1 bg-[var(--pg-border-2)]" />
        {btn(editor.isActive('bold'), false, () => editor.chain().focus().toggleBold().run(), 'Bold', Bold, 'rte-bold')}
        {btn(editor.isActive('italic'), false, () => editor.chain().focus().toggleItalic().run(), 'Italic', Italic, 'rte-italic')}
        {btn(editor.isActive('underline'), false, () => editor.chain().focus().toggleUnderline().run(), 'Underline', UnderlineIcon, 'rte-underline')}
        {btn(editor.isActive('highlight'), false, () => editor.chain().focus().toggleHighlight().run(), 'Highlight', Highlighter, 'rte-highlight')}
        <span className="w-px h-5 mx-1 bg-[var(--pg-border-2)]" />
        {btn(editor.isActive('bulletList'), false, () => editor.chain().focus().toggleBulletList().run(), 'Bulleted list', List, 'rte-ul')}
        {btn(editor.isActive('orderedList'), false, () => editor.chain().focus().toggleOrderedList().run(), 'Numbered list', ListOrdered, 'rte-ol')}
        {btn(editor.isActive('blockquote'), false, () => editor.chain().focus().toggleBlockquote().run(), 'Quote', Quote, 'rte-quote')}
        <span className="w-px h-5 mx-1 bg-[var(--pg-border-2)]" />
        {btn(editor.isActive('link'), false, openExternalLink, 'External link', Link2, 'rte-link-external')}
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={openInternalLink}
          data-testid="rte-link-internal"
          aria-label="Insert internal link"
          className="px-2 py-1.5 rounded-[var(--pg-radius-sm)] text-[10px] font-mono tracking-widest border border-[var(--pg-border-2)] text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)] inline-flex items-center gap-1">
          <Link2 size={13} /> INTERNAL
        </button>
        {editor.isActive('link') && btn(false, false, () => editor.chain().focus().unsetLink().run(), 'Remove link', Link2Off, 'rte-link-remove')}
        <span className="w-px h-5 mx-1 bg-[var(--pg-border-2)]" />
        {btn(false, !editor.can().undo(), () => editor.chain().focus().undo().run(), 'Undo', Undo2, 'rte-undo')}
        {btn(false, !editor.can().redo(), () => editor.chain().focus().redo().run(), 'Redo', Redo2, 'rte-redo')}
      </div>

      <div
        className="pg-card !p-4"
        style={{ minHeight }}
        data-testid="rich-text-editor-content"
        onClick={() => editor.commands.focus()}
      >
        <EditorContent editor={editor} className="pg-prose focus:outline-none" />
      </div>

      {linkModal && (
        <LinkModal
          modal={linkModal}
          onCancel={() => setLinkModal(null)}
          onApply={applyLink}
        />
      )}
    </div>
  );
}

// ============================================================
//  Link picker modal (external URL + internal content search)
// ============================================================
function LinkModal({ modal, onCancel, onApply }) {
  const [tab, setTab] = useState(modal.mode);
  const [url, setUrl] = useState(modal.url || '');
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);

  useEffect(() => {
    let alive = true;
    if (tab !== 'internal') return;
    (async () => {
      try {
        // Query each content type in parallel; take a few from each.
        const [channels, articles, products, reads] = await Promise.all([
          api.get('/channels').catch(() => ({ data: [] })),
          api.get('/articles', { params: { limit: 30 } }).catch(() => ({ data: [] })),
          api.get('/products', { params: { limit: 30 } }).catch(() => ({ data: [] })),
          api.get('/mobile_reads').catch(() => ({ data: [] })),
        ]);
        if (!alive) return;
        const items = [
          ...(channels.data || []).map((c) => ({ type: 'channel', id: c.key || c.id, label: c.label || c.title || c.key, url: c.route })),
          ...(articles.data || []).map((a) => ({ type: 'article', id: a.id, label: a.title, url: `/on-my-mind/${a.slug || a.id}` })),
          ...(products.data || []).map((p) => ({ type: 'product', id: p.id, label: p.title || p.name, url: `/vault/${p.id}` })),
          ...(reads.data || []).map((r) => ({ type: 'mobile_read', id: r.id, label: r.title, url: `/mobile-reads/${r.id}` })),
        ].filter((x) => x.url && x.label);
        setResults(items);
      } catch (e) {
        setResults([]);
      }
    })();
    return () => { alive = false; };
  }, [tab]);

  const filtered = useMemo(() => {
    if (!q) return results;
    const needle = q.toLowerCase();
    return results.filter((r) => r.label.toLowerCase().includes(needle) || r.type.toLowerCase().includes(needle));
  }, [q, results]);

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" style={{ background: 'rgba(10,8,16,0.75)' }} onClick={onCancel}>
      <div className="pg-card w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid="rte-link-modal">
        <div className="flex items-center justify-between mb-4">
          <p className="pg-label">INSERT LINK</p>
          <button type="button" onClick={onCancel} aria-label="Close" className="text-[var(--pg-ink-muted)] hover:text-[var(--pg-ink)]"><X size={16} /></button>
        </div>

        <div className="flex items-center gap-1 mb-4">
          <button type="button" onClick={() => setTab('external')} className={`px-3 py-1.5 rounded-full text-[10px] font-mono tracking-widest uppercase border ${tab === 'external' ? 'bg-[rgba(255,62,165,0.14)] border-[var(--pg-neon-pink)] text-[var(--pg-neon-pink)]' : 'border-[var(--pg-border-2)] text-[var(--pg-ink-muted)]'}`} data-testid="rte-link-tab-external">External</button>
          <button type="button" onClick={() => setTab('internal')} className={`px-3 py-1.5 rounded-full text-[10px] font-mono tracking-widest uppercase border ${tab === 'internal' ? 'bg-[rgba(255,62,165,0.14)] border-[var(--pg-neon-pink)] text-[var(--pg-neon-pink)]' : 'border-[var(--pg-border-2)] text-[var(--pg-ink-muted)]'}`} data-testid="rte-link-tab-internal">Internal</button>
        </div>

        {tab === 'external' ? (
          <div>
            <label className="text-xs font-mono tracking-widest text-[var(--pg-ink-muted)]">External URL</label>
            <input
              autoFocus
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com"
              className="mt-1 w-full pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-sm"
              data-testid="rte-link-external-input"
            />
            <div className="flex justify-end gap-2 mt-4">
              {modal.url && <button type="button" onClick={() => onApply('')} className="pg-btn-secondary rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-xs">Remove link</button>}
              <button type="button" onClick={onCancel} className="pg-btn-secondary rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-xs">Cancel</button>
              <button type="button" onClick={() => onApply(url.trim())} disabled={!url.trim()} className="pg-btn-primary rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-xs disabled:opacity-50" data-testid="rte-link-external-apply">Insert</button>
            </div>
          </div>
        ) : (
          <div>
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search channels, articles, products, mobile reads…"
              className="w-full pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-sm mb-3"
              data-testid="rte-link-internal-search"
            />
            <ul className="max-h-72 overflow-auto divide-y divide-[var(--pg-border-2)]" data-testid="rte-link-internal-list">
              {filtered.length === 0 && (
                <li className="p-3 text-xs text-[var(--pg-ink-muted)]">No matches.</li>
              )}
              {filtered.map((r) => (
                <li key={`${r.type}:${r.id}`}>
                  <button
                    type="button"
                    onClick={() => onApply(r.url, { internal: true })}
                    className="w-full text-left p-3 hover:bg-[rgba(255,255,255,0.04)] flex items-center gap-3"
                    data-testid={`rte-link-internal-pick-${r.type}-${r.id}`}
                  >
                    <span className="text-[9px] font-mono tracking-widest text-[var(--pg-ink-faint)] uppercase w-20">{r.type.replace('_', ' ')}</span>
                    <span className="text-sm text-[var(--pg-ink)] flex-1 truncate">{r.label}</span>
                    <span className="text-[10px] font-mono text-[var(--pg-ink-faint)]">{r.url}</span>
                  </button>
                </li>
              ))}
            </ul>
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" onClick={onCancel} className="pg-btn-secondary rounded-[var(--pg-radius-sm)] px-3 py-1.5 text-xs">Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
